import { FieldValues, UseFormReturn, Path, PathValue } from "react-hook-form";
import { cn } from "@/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Tags } from "@/components/Common/Tags";
import { Tag } from "@/types/v2";
import { sortTagsByShopOrder } from "@/lib/tags";

export type TagsFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: PathValue<TFieldValues, Path<TFieldValues>>;
  onChange?: (tags: string[]) => void;
  maxTags?: number;
  className?: string;
  tagClassName?: string;
  tagsContainerClassName?: string;
  suggestedTags?: string[];
  suggestedTagsContainerClassName?: string;
  maxSuggestedTags?: number;
  allTags?: Tag[];
};

export const TagsField = <TFieldValues extends FieldValues = FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
  disabled,
  defaultValue = [] as PathValue<TFieldValues, Path<TFieldValues>>,
  onChange,
  maxTags,
  className,
  tagClassName,
  tagsContainerClassName,
  suggestedTags,
  suggestedTagsContainerClassName,
  maxSuggestedTags,
  allTags,
}: TagsFieldProps<TFieldValues>) => {
  const handleTagCreate = (tag: string) => {
    const currentTags = (form.getValues(name) || []) as string[];

    // Check if we've reached the maximum number of tags
    if (maxTags !== undefined && currentTags.length >= maxTags) {
      return;
    }

    // Check if the tag already exists
    if (currentTags.includes(tag)) {
      return;
    }

    const newTags = sortTagsByShopOrder([...currentTags, tag], allTags);
    form.setValue(
      name,
      newTags as PathValue<TFieldValues, Path<TFieldValues>>,
      { shouldValidate: true }
    );

    if (onChange) {
      onChange(newTags);
    }
  };

  const handleTagRemove = (index: number) => {
    const currentTags = form.getValues(name) || [];
    const newTags = currentTags.filter((_, i) => i !== index);
    form.setValue(
      name,
      newTags as PathValue<TFieldValues, Path<TFieldValues>>,
      { shouldValidate: true }
    );

    if (onChange) {
      onChange(newTags);
    }
  };

  return (
    <FormField
      control={form.control}
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Tags
              tags={field.value || []}
              onTagCreate={handleTagCreate}
              onTagRemove={handleTagRemove}
              placeholder={placeholder}
              disabled={disabled}
              maxTags={maxTags}
              tagClassName={tagClassName}
              tagsContainerClassName={tagsContainerClassName}
              suggestedTags={suggestedTags}
              suggestedTagsContainerClassName={suggestedTagsContainerClassName}
              maxSuggestedTags={maxSuggestedTags}
              allTags={allTags}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
